import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Grid,
  Card,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import EmptyData from "components/EmptyData";
import SubscriptionPlanModel from "DialogBox/subscriptionPlanModel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import apiClient from "../../Utils/apiClient";

const SubscriptionPlanMode = () => {
  const [allData, setAllData] = useState([]);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (!user) navigate("/authentication/sign-in");
  }, [navigate]);

  const fetchPlans = async () => {
    try {
      const res = await apiClient.get("/plans");
      const data = res.data || [];
      const plansBySub = {};

      data.forEach((plan) => {
        const subType = plan.subscriptionType || "Basic";
        if (!plansBySub[subType]) {
          plansBySub[subType] = {
            id: subType, // Using subscriptionType as the unified ID
            name: subType, // The display name for the plan group
            features: (plan.features || []).map((f) => {
              const desc = String(f.description || "").trim();
              let included = true;
              let textValue = "";
              if (desc === "unavailable" || desc === "false") {
                included = false;
              } else if (desc !== "available" && desc !== "true" && desc !== "") {
                textValue = desc;
              }
              return { name: f.title, included, textValue };
            }),
            prices: { monthly: 0, quarterly: 0, yearly: 0 },
            planIds: {}, // to keep track of backend document IDs for deletion
          };
        }

        const type = String(plan.planType || "Monthly").toLowerCase();
        if (type.includes("month")) {
          plansBySub[subType].prices.monthly = plan.price;
          plansBySub[subType].planIds.monthly = plan._id || plan.id;
        } else if (type.includes("quarter")) {
          plansBySub[subType].prices.quarterly = plan.price;
          plansBySub[subType].planIds.quarterly = plan._id || plan.id;
        } else if (type.includes("year")) {
          plansBySub[subType].prices.yearly = plan.price;
          plansBySub[subType].planIds.yearly = plan._id || plan.id;
        }
      });

      const sortedPlans = Object.values(plansBySub).sort((a, b) =>
        a.prices.monthly - b.prices.monthly
      );
      setAllData(sortedPlans);
    } catch (error) {
      toast.error("Failed to load plans from server");
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleFormSubmit = async (data) => {
    const subType = data.name;

    const backendFeatures = (data.features || []).map((f) => {
      let desc = "";
      if (!f.included) {
        desc = "unavailable";
      } else if (f.textValue) {
        desc = f.textValue;
      } else {
        desc = "available";
      }
      return {
        title: f.name,
        description: desc,
      };
    });

    const monthlyPayload = {
      name: `${subType} Monthly`,
      subscriptionType: subType,
      planType: "Monthly",
      price: Number(data.prices.monthly || 0),
      features: backendFeatures,
    };

    const quarterlyPayload = {
      name: `${subType} Quarterly`,
      subscriptionType: subType,
      planType: "Quarterly",
      price: Number(data.prices.quarterly || 0),
      features: backendFeatures,
    };

    const yearlyPayload = {
      name: `${subType} Yearly`,
      subscriptionType: subType,
      planType: "Yearly",
      price: Number(data.prices.yearly || 0),
      features: backendFeatures,
    };

    const payload = [monthlyPayload, quarterlyPayload, yearlyPayload];

    try {
      await apiClient.post("/subscriptions/plans/bulk", payload);
      toast.success(`Subscription plan ${data.id ? "updated" : "created"} successfully`);
      setOpen(false);
      setEditData(null);
      fetchPlans();
    } catch (error) {
      toast.error("Failed to save subscription plan");
    }
  };

  const handleUpdate = (item) => {
    setEditData(item);
    setOpen(true);
  };

  const handleDelete = async (item) => {
    try {
      const { planIds } = item;
      const idsToDelete = Object.values(planIds || {}).filter((id) => id);

      if (idsToDelete.length > 0) {
        await Promise.all(idsToDelete.map((id) => apiClient.delete(`/plans/${id}`)));
      }
      toast.success("Subscription plan deleted");
      fetchPlans();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  return (
    <DashboardLayout>
      <MDBox pt={8} pb={3}>
        <button
          className="btn btn-success ms-3 mb-5"
          onClick={() => {
            setEditData(null);
            setOpen(true);
          }}
        >
          Add Subscription Plan
        </button>

        <SubscriptionPlanModel
          open={open}
          onClose={() => {
            setOpen(false);
            setEditData(null);
          }}
          onSubmit={handleFormSubmit}
          editData={editData}
        />

        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                textAlign="center"
              >
                <ToastContainer />
                <MDTypography variant="h6" color="white">
                  SUBSCRIPTION PLANS
                </MDTypography>
              </MDBox>

              {allData.length ? (
                <TableContainer component={Paper}>
                  <Table sx={{ "& td, & th": { fontSize: "13px", padding: "8px" } }}>
                    <TableBody>
                      <TableRow>
                        {[
                          "SR",
                          "Name",
                          "Features",
                          "Monthly",
                          "Quarterly",
                          "Yearly",
                          "Actions",
                        ].map((h) => (
                          <TableCell
                            key={h}
                            align="center"
                            sx={{ fontWeight: "bold", color: "#1C87F9" }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>

                      {allData.map((item, index) => (
                        <TableRow key={item.id}>
                          <TableCell align="center">{index + 1}</TableCell>
                          <TableCell align="center">{item.name}</TableCell>

                          <TableCell align="left">
                            <ul style={{ paddingLeft: 18, margin: 0 }}>
                              {item.features?.map((feature, i) => (
                                <li
                                  key={i}
                                  style={{
                                    fontSize: 13,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  {feature.included ? (
                                    <CheckCircleIcon fontSize="small" color="success" />
                                  ) : (
                                    <CancelIcon fontSize="small" color="error" />
                                  )}
                                  {feature.name} {feature.included && feature.textValue ? <strong style={{color:"#00838F", marginLeft: 4}}>({feature.textValue})</strong> : null}
                                </li>
                              ))}
                            </ul>
                          </TableCell>

                          <TableCell align="center">₦{item?.prices?.monthly}</TableCell>
                          <TableCell align="center">₦{item?.prices?.quarterly}</TableCell>
                          <TableCell align="center">₦{item?.prices?.yearly}</TableCell>

                          <TableCell align="center">
                            <button
                              className="btn btn-warning btn-sm me-2"
                              onClick={() => handleUpdate(item)}
                            >
                              Update
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDelete(item)}
                            >
                              Delete
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyData />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default SubscriptionPlanMode;
